import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './TopNavBar.css';
import SiteSearch from './search/SiteSearch';

const TOOLS = [
  { to: '/custom-builder', label: 'Custom Bundle Builder' },
  { to: '/compare-screens', label: 'Garmin Screen Finder' },
  { to: '/select/battery', label: 'Battery Finder' },
];

function linkClass({ isActive }) {
  return `nav-link${isActive ? ' is-active' : ''}`;
}

const TopNavBar = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const navRef = useRef(null);
  const toolsButtonRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    setToolsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen && !toolsOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      setToolsOpen(false);
    };

    const onPointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setToolsOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [menuOpen, toolsOpen]);

  const closeAll = () => {
    setMenuOpen(false);
    setToolsOpen(false);
  };

  return (
    <nav
      className={`top-navbar${isHomePage ? ' top-navbar--home' : ''}`}
      aria-label="Primary"
      ref={navRef}
    >
      <div className="navbar-container">
        <div className="nav-logo">
          <Link to="/" className="logo-link" onClick={closeAll}>
            <img
              src="/img/logo-full-white-text.svg"
              alt="Angler Built"
              className="logo logo-desktop"
            />
            <img
              src="/img/logo-icon.svg"
              alt="Angler Built"
              className="logo logo-mobile"
            />
          </Link>
        </div>

        {!isHomePage && <SiteSearch/>}

        <button
          type="button"
          className="nav-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="primary-nav-links"
          data-testid="nav-menu-toggle"
          onClick={() => {
            setMenuOpen((open) => !open);
            setToolsOpen(false);
          }}
        >
          {menuOpen ? 'Close' : 'Menu'}
        </button>

        {menuOpen ? (
          <button
            type="button"
            className="nav-backdrop"
            aria-label="Close menu"
            tabIndex={-1}
            onClick={closeAll}
          />
        ) : null}

        <div
          id="primary-nav-links"
          className={`nav-links${menuOpen ? ' is-open' : ''}`}
        >
          <NavLink
            to="/compare"
            className={({ isActive }) =>
              linkClass({
                isActive:
                  isActive || location.pathname.startsWith('/comparisons'),
              })
            }
            onClick={closeAll}
          >
            Compare
          </NavLink>
          <NavLink to="/compatibility" className={linkClass} onClick={closeAll}>
            Compatibility
          </NavLink>
          <NavLink to="/price-comparison" className={linkClass} onClick={closeAll}>
            Prices
          </NavLink>
          <NavLink to="/ice-fishing" className={({ isActive }) => linkClass({ isActive: isActive && location.pathname !== '/ice-fishing/shows' })} onClick={closeAll}>
            Ice Fishing
          </NavLink>
          <NavLink to="/ice-fishing/shows" className={linkClass} onClick={closeAll}>
            Ice Shows
          </NavLink>

          <div className={`nav-dropdown${toolsOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="dropdown-toggle"
              aria-expanded={toolsOpen}
              aria-haspopup="true"
              aria-controls="nav-tools-menu"
              data-testid="nav-tools-toggle"
              ref={toolsButtonRef}
              onClick={() => setToolsOpen((open) => !open)}
            >
              Tools
            </button>
            <div
              id="nav-tools-menu"
              className="dropdown-menu"
              hidden={!toolsOpen}
            >
              {TOOLS.map((tool) => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  className="dropdown-item"
                  onClick={closeAll}
                >
                  {tool.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNavBar;
